import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Container,
  Typography,
  Paper,
  InputAdornment,
  Stack,
  Card,
  CardContent,
  Fade,
  Alert,
  Link as MuiLink,
} from "@mui/material";
import {
  Email,
  LockReset,
  ArrowBack,
  Send,
  Security,
  CheckCircle,
  Info,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import * as Yup from "yup";
import RowadLogo from "../RowadLogo";
import LoadingButton from "@mui/lab/LoadingButton";

const MySwal = withReactContent(Swal);

const EnhancedResetPassword = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const onSubmit = (values) => {
    setIsLoading(true);

    axios
      .post(`${process.env.REACT_APP_Back_URL}/userst/reset-password`, {
        emailaddress: values.email,
        new_reset: true,
      })
      .then((resp) => {
        if (resp.status === 200) {
          MySwal.fire({
            icon: resp.data.status,
            title: resp.data.status === "success" ? "Email Sent Successfully! ✉️" : "Email Not Found",
            text: resp.data.status === "success"
              ? "Please check your email inbox for password reset instructions"
              : "This email doesn't exist in our system. Would you like to create a new account?",
            showConfirmButton: true,
            confirmButtonText: resp.data.status === "success" ? "Got it!" : "Create Account",
            confirmButtonColor: "#B41736",
            background: "#51647C",
            color: "white",
          }).then((result) => {
            if (result.isConfirmed) {
              resp.data.status === "success" ? navigate("/sign-in") : navigate("/sign-up");
            }
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        MySwal.fire({
          icon: "error",
          title: "Request Failed",
          text: error.message || "Something went wrong. Please try again.",
          confirmButtonColor: "#B41736",
        });
        setIsLoading(false);
      });
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._%+-]+@(rowad-rme|rme-fitout|rme-g|driller-eg|rowad-rme\.net|g-buildings|rowad-investment|tetraes|orascom)\.(com|net)$/,
        "Invalid email - must be from an authorized domain"
      ),
  });

  const initialValues = {
    email: "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#51647C",
        position: "relative",
        overflow: "hidden",
        py: 4,
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          animation: "float 6s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          right: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          animation: "float 8s ease-in-out infinite",
        }}
      />

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) translateX(0px); }
            50% { transform: translateY(-20px) translateX(20px); }
          }
        `}
      </style>

      <Container maxWidth="sm">
        <Fade in={isVisible} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Header Section */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box sx={{ mb: 3 }}>
                <RowadLogo />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                <LockReset sx={{ fontSize: 32, mr: 1, color: "#51647C" }} />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#51647C",
                  }}
                >
                  Reset Password
                </Typography>
              </Box>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Don't worry! We'll send you reset instructions via email
              </Typography>

              {/* Security Badge */}
              <Card sx={{ bgcolor: "error.light", color: "white", mb: 3 }}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    <Security sx={{ fontSize: 20 }} />
                    <Typography variant="body2">
                      Secure password reset process
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Info Alert */}
              <Alert 
                severity="info" 
                icon={<Info />}
                sx={{ 
                  borderRadius: 2,
                  textAlign: "left",
                  mb: 3
                }}
              >
                <Typography variant="body2">
                  Enter your company email address and we'll send you a link to reset your password
                </Typography>
              </Alert>
            </Box>

            {/* Reset Form */}
            <form onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                {/* Email Field */}
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Company Email Address"
                  placeholder="your.email@company.com"
                  {...formik.getFieldProps("email")}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(240, 147, 251, 0.15)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 20px rgba(240, 147, 251, 0.25)",
                      },
                    },
                  }}
                />

                {/* Submit Button */}
                <LoadingButton
                  fullWidth
                  size="large"
                  variant="contained"
                  loading={isLoading}
                  type="submit"
                  startIcon={<Send />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: "1rem",
                    fontWeight: 600,
                    background: "#B41736",
                    boxShadow: "0 4px 15px rgba(180, 23, 54, 0.4)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "#8f1129",
                      boxShadow: "0 6px 20px rgba(180, 23, 54, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "grey.300",
                    },
                  }}
                >
                  Send Reset Link
                </LoadingButton>

                {/* Back to Login */}
                <Box sx={{ textAlign: "center" }}>
                  <MuiLink
                    component={Link}
                    to="/sign-in"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <ArrowBack fontSize="small" />
                    Back to Sign In
                  </MuiLink>
                </Box>
              </Stack>
            </form>

            {/* Additional Help */}
            <Box
              sx={{
                mt: 4,
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 1 }}>
                <CheckCircle sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5, color: "success.main" }} />
                Need Help?
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", display: "block" }}>
                If you don't receive the email within 5 minutes, please check your spam folder or{" "}
                <MuiLink component={Link} to="/sign-up" sx={{ fontWeight: 600 }}>
                  create a new account
                </MuiLink>
              </Typography>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} ROWAD RME. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default EnhancedResetPassword;
